import React from 'react';
import styles from './ShowDocumentModal.module.css';

/**
 * ShowDocumentModal - Full-Screen Document Viewer Modal
 * 
 * Displays PDF documents and other embeddable content in a full-screen modal overlay.
 * Commonly used for showing reference materials, course documents, and educational resources.
 * 
 * Features:
 * - Full-screen overlay with darkened background
 * - Close button in header
 * - Click-outside-to-close functionality
 * - Responsive iframe for document rendering
 * - Accessibility support with aria-label
 * 
 * @component
 * @param {Object} props - Component properties
 * @param {boolean} props.open - Controls modal visibility (true = visible, false = hidden)
 * @param {Function} props.onClose - Callback function invoked when user closes the modal
 * @param {string} props.documentURL - URL of the document to display in iframe
 *                                      (supports PDF, HTML, or any embeddable content)
 * 
 * @returns {JSX.Element|null} Modal component or null if not open
 * 
 * @example
 * // PDF document modal
 * const [modalOpen, setModalOpen] = useState(false);
 * 
 * <ShowDocumentModal 
 *   open={modalOpen} 
 *   onClose={() => setModalOpen(false)} 
 *   documentURL="/documents/heat-pump-manual.pdf" 
 * />
 * 
 * @example
 * // Embedded web content
 * <ShowDocumentModal 
 *   open={true} 
 *   onClose={handleClose} 
 *   documentURL="https://example.com/interactive-diagram" 
 * />
 */
export function ShowDocumentModal({ open, onClose, documentURL }) {
  // Don't render anything if modal is closed
  if (!open) return null;

  /**
   * Handles clicks on the overlay background
   * Closes modal only if user clicks the overlay itself (not the modal content)
   * @param {React.MouseEvent} e - Click event
   */
  const handleOverlayClick = (e) => {
    // Only close if clicking the overlay background (not propagated from modal content)
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        {/* Modal header with close button */}
        <div className={styles.header}>
          <button 
            className={styles.closeButton} 
            onClick={onClose} 
            aria-label="Close modal"
          >
            ×
          </button>
        </div>
        
        {/* Document content area */}
        <div className={styles.content}>
          {/* Render iframe only if documentURL is provided */}
          {documentURL && (
            <iframe 
              src={documentURL} 
              className={styles.iframe} 
              title="Document Viewer" 
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default ShowDocumentModal;
